import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Plus, Search, Users, ArrowRight, Trash2, X, Mail, Loader2, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const AdminCoaches = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(null);
  const [selectedCoaches, setSelectedCoaches] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [form, setForm] = useState({
    email: '',
    full_name: '',
    phone: '',
    specialization: '',
    bio: '',
    message: ''
  });

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      const response = await API.get('/admin/coaches');
      setCoaches(response.data);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoach = async (e) => {
    e.preventDefault();
    if (!form.email || !form.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      await API.post('/admin/coaches', form);
      toast.success('Coach created! Setup email sent.');
      setShowModal(false);
      setForm({ email: '', full_name: '', phone: '', specialization: '', bio: '', message: '' });
      fetchCoaches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coach');
    } finally {
      setCreating(false);
    }
  };

  const handleResendInvite = async (coachId) => {
    setResendingInvite(coachId);
    try {
      await API.post(`/admin/coaches/${coachId}/resend-invite`);
      toast.success('Setup email resent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setResendingInvite(null);
    }
  };

  const handleDeleteCoach = async (coachId, coachName) => {
    if (!window.confirm(`Delete coach "${coachName}"?\n\nThis will unlink all their students.`)) return;
    try {
      await API.delete(`/admin/coaches/${coachId}`);
      toast.success('Coach deleted');
      fetchCoaches();
    } catch (error) {
      toast.error('Failed to delete coach');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedCoaches.length) return;
    if (!window.confirm(`Delete ${selectedCoaches.length} coach(es)? Their students will be unlinked.`)) return;
    setBulkDeleting(true);
    try {
      await API.post('/admin/coaches/bulk-delete', { ids: selectedCoaches });
      toast.success(`${selectedCoaches.length} coach(es) deleted`);
      setSelectedCoaches([]);
      fetchCoaches();
    } catch (error) {
      toast.error('Failed to delete coaches');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleCoachSelect = (id) => {
    setSelectedCoaches(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedCoaches.length === filteredCoaches.length) {
      setSelectedCoaches([]);
    } else {
      setSelectedCoaches(filteredCoaches.map(c => c.id));
    }
  };

  const filteredCoaches = coaches.filter(coach =>
    coach.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coach.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-coaches">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-2">Coaches</h1>
          <p className="text-[#57534E]">Manage coach accounts and their students.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#2C5530] text-white font-semibold hover:bg-[#1E3A21] transition-colors"
          data-testid="create-coach-btn"
        >
          <Plus className="w-5 h-5" />
          Invite Coach
        </button>
      </div>

      {/* Search + Bulk Actions */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
          <input
            type="text"
            placeholder="Search coaches..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-[#E7E5E4] bg-white text-[#1A1A1A] placeholder:text-[#737373] focus:border-[#2C5530] focus:outline-none"
            data-testid="search-coaches"
          />
        </div>
      </div>

      {/* Bulk Delete Bar */}
      {selectedCoaches.length > 0 && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-red-50 border border-red-200">
          <span className="text-sm font-medium text-red-700">{selectedCoaches.length} selected</span>
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
            data-testid="bulk-delete-coaches-btn"
          >
            <Trash2 className="w-4 h-4" />
            {bulkDeleting ? 'Deleting...' : 'Delete Selected'}
          </button>
          <button onClick={() => setSelectedCoaches([])} className="text-sm text-[#737373] hover:text-[#1A1A1A]">Clear</button>
        </div>
      )}

      {/* Coaches List */}
      {filteredCoaches.length > 0 ? (
        <div className="space-y-3">
          {/* Select All */}
          <div className="flex items-center gap-2 px-1">
            <input
              type="checkbox"
              checked={selectedCoaches.length === filteredCoaches.length && filteredCoaches.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 accent-[#2C5530]"
              data-testid="select-all-coaches"
            />
            <span className="text-sm text-[#737373]">Select all</span>
          </div>

          {filteredCoaches.map((coach) => (
            <div
              key={coach.id}
              className={`bg-white border p-4 sm:p-5 transition-all ${selectedCoaches.includes(coach.id) ? 'border-red-300 bg-red-50/30' : 'border-[#E7E5E4] hover:border-[#2C5530]/30 hover:shadow-sm'}`}
              data-testid={`coach-row-${coach.id}`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={selectedCoaches.includes(coach.id)}
                  onChange={() => toggleCoachSelect(coach.id)}
                  className="w-4 h-4 accent-[#2C5530] mt-1 flex-shrink-0"
                  data-testid={`select-coach-${coach.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-[#2C5530] text-white flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1A1A1A] truncate">{coach.full_name}</p>
                        <p className="text-sm text-[#737373] truncate">{coach.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {coach.status === 'pending_setup' && (
                        <button
                          onClick={() => handleResendInvite(coach.id)}
                          disabled={resendingInvite === coach.id}
                          className="p-2 text-[#2C5530] hover:bg-[#F2F0E9] transition-colors disabled:opacity-50"
                          title="Resend setup email"
                          data-testid={`resend-invite-${coach.id}`}
                        >
                          {resendingInvite === coach.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        </button>
                      )}
                      <Link
                        to={`/admin/coaches/${coach.id}`}
                        className="text-[#2C5530] font-semibold text-sm hover:underline flex items-center gap-1"
                        data-testid={`view-coach-${coach.id}`}
                      >
                        View <ArrowRight className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDeleteCoach(coach.id, coach.full_name)}
                        className="p-2 text-red-500 hover:bg-red-50 transition-colors"
                        data-testid={`delete-coach-${coach.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {/* Meta row - visible on all sizes */}
                  <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-[#F2F0E9]">
                    {coach.status === 'pending_setup' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 text-xs font-medium">
                        <Clock className="w-3 h-3" />Pending Setup
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />Active
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-sm text-[#57534E]">
                      <Users className="w-3.5 h-3.5 text-[#737373]" />
                      <strong>{coach.total_clients}</strong> students
                    </span>
                    <span className="text-xs text-[#737373]">
                      Created {formatDate(coach.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : searchTerm ? (
        <div className="bg-white border border-[#E7E5E4] p-12 text-center">
          <Search className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
          <p className="text-[#57534E]">No coaches match "{searchTerm}"</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E7E5E4] p-12 text-center">
          <GraduationCap className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
          <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">No Coaches Yet</h3>
          <p className="text-[#57534E] mb-6">Create your first coach account to get started.</p>
          <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 px-6 py-3 bg-[#2C5530] text-white font-semibold hover:bg-[#1E3A21] transition-colors">
            <Plus className="w-5 h-5" />Invite Coach
          </button>
        </div>
      )}

      {/* Create Coach Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)}></div>
          <div className="relative bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 md:p-8" data-testid="create-coach-modal">
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 p-2 text-[#737373] hover:text-[#1A1A1A]">
              <X className="w-5 h-5" />
            </button>

            <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Invite Coach</h2>
            <p className="text-[#57534E] mb-6">Add a new coach. They'll receive an email to set their password and activate their account.</p>

            <form onSubmit={handleCreateCoach} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Full Name *</label>
                  <input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full px-4 py-3 border border-[#E7E5E4] bg-white text-[#1A1A1A] focus:border-[#2C5530] focus:outline-none" placeholder="John Smith" required data-testid="coach-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Email Address *</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 border border-[#E7E5E4] bg-white text-[#1A1A1A] focus:border-[#2C5530] focus:outline-none" placeholder="coach@example.com" required data-testid="coach-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Phone</label>
                  <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 border border-[#E7E5E4] bg-white text-[#1A1A1A] focus:border-[#2C5530] focus:outline-none" placeholder="+44 7123 456789" data-testid="coach-phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Specialization</label>
                  <input type="text" value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} className="w-full px-4 py-3 border border-[#E7E5E4] bg-white text-[#1A1A1A] focus:border-[#2C5530] focus:outline-none" placeholder="Mental Performance" data-testid="coach-specialization-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Bio</label>
                  <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full px-4 py-3 border border-[#E7E5E4] bg-white text-[#1A1A1A] focus:border-[#2C5530] focus:outline-none resize-none" rows={2} placeholder="Brief bio about the coach..." data-testid="coach-bio-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Personal Message <span className="text-[#737373] font-normal">(optional)</span></label>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 border border-[#E7E5E4] bg-white text-[#1A1A1A] focus:border-[#2C5530] focus:outline-none resize-none" rows={3} placeholder="Add a personal welcome note that will be included in their setup email..." data-testid="coach-message-input" />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 border border-[#E7E5E4] text-[#57534E] font-semibold hover:bg-[#F2F0E9] transition-colors">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#2C5530] text-white font-semibold hover:bg-[#1E3A21] transition-colors disabled:opacity-50" data-testid="submit-create-coach">
                  {creating ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Creating...</>) : 'Invite Coach'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoaches;
